import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Tooltip,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Fade,
  CircularProgress,
  LinearProgress,
  Avatar,
  Badge,
  useTheme,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  Speed,
  DataObject,
  FilterAlt,
  Schedule,
  Download,
  CheckCircle,
  Error,
  Warning,
  Info,
  Close,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

// Animation keyframes for data flow
const flowAnimation = keyframes`
  0% { transform: translateX(-100%) scale(0); opacity: 0; }
  50% { transform: translateX(0%) scale(1); opacity: 1; }
  100% { transform: translateX(100%) scale(0); opacity: 0; }
`;

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 246, 255, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(0, 246, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 246, 255, 0); }
`;

const WorkflowView = ({ nodes = [], edges = [], executionState = {}, onExecuteNode }) => {
  const theme = useTheme();
  const [animatingFlow, setAnimatingFlow] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);

  // Pan and zoom state
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);

  // Resize handle state for splitting workflow and node inspector
  const [inspectorHeight, setInspectorHeight] = useState(500);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartY, setResizeStartY] = useState(0);
  const containerRef = useRef(null);

  // Auto-select the first node by default
  useEffect(() => {
    if (nodes.length > 0 && !selectedNode) {
      setSelectedNode(nodes[0].id);
    }
  }, [nodes, selectedNode]);

  // Global mouse event listeners for pan functionality with proper event handling
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging && nodes.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        setTransform((prev) => ({
          ...prev,
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        }));
      }
    };

    const handleGlobalMouseUp = (e) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
      }
    };

    if (isDragging && nodes.length > 0) {
      // Use addEventListener with explicit options to avoid passive listener warnings
      document.addEventListener("mousemove", handleGlobalMouseMove, { passive: false });
      document.addEventListener("mouseup", handleGlobalMouseUp, { passive: false });
      // Prevent text selection during drag
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    } else {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove, { passive: false });
      document.removeEventListener("mouseup", handleGlobalMouseUp, { passive: false });
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging, dragStart, nodes.length]);

  // Pan and zoom handlers with useCallback to prevent passive listener warnings
  const handleWheel = useCallback(
    (e) => {
      if (nodes.length === 0) return;

      const delta = e.deltaY * -0.001;
      const newScale = Math.min(Math.max(0.1, transform.scale + delta), 3);

      setTransform((prev) => ({
        ...prev,
        scale: newScale,
      }));
    },
    [transform.scale, nodes.length]
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (nodes.length === 0) return;

      // Enable panning with left or middle mouse button
      // Allow panning anywhere in the canvas except on interactive elements
      if (
        (e.button === 0 || e.button === 1) &&
        !e.target.closest('[role="tooltip"]') && // Exclude tooltips
        !e.target.closest("button") && // Exclude buttons
        !e.target.closest(".MuiIconButton-root") && // Exclude icon buttons
        !e.target.closest(".MuiChip-root") && // Exclude chips
        !e.target.closest("svg") && // Exclude SVG elements (connections)
        !e.target.closest(".workflow-node") && // Exclude workflow nodes specifically
        e.target.closest('[data-workflow-canvas="true"]') // Only within the workflow canvas
      ) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setDragStart({
          x: e.clientX - transform.x,
          y: e.clientY - transform.y,
        });
      }
    },
    [transform.x, transform.y, nodes.length]
  );

  // Zoom controls
  const zoomIn = () => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.min(prev.scale * 1.2, 3),
    }));
  };

  const zoomOut = () => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.max(prev.scale * 0.8, 0.1),
    }));
  };

  const resetView = () => {
    setTransform({ x: 0, y: 0, scale: 1 });
  };

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "=" || e.key === "+") {
          e.preventDefault();
          zoomIn();
        } else if (e.key === "-") {
          e.preventDefault();
          zoomOut();
        } else if (e.key === "0") {
          e.preventDefault();
          resetView();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Wheel event listener with proper passive handling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || nodes.length === 0) return;

    const handleWheelEvent = (e) => {
      e.preventDefault();
      const delta = e.deltaY * -0.001;
      const newScale = Math.min(Math.max(0.1, transform.scale + delta), 3);

      setTransform((prev) => ({
        ...prev,
        scale: newScale,
      }));
    };

    // Add wheel event listener with explicit passive: false to allow preventDefault
    canvas.addEventListener("wheel", handleWheelEvent, { passive: false });

    return () => {
      canvas.removeEventListener("wheel", handleWheelEvent, { passive: false });
    };
  }, [transform.scale, nodes.length]);

  // Resize handlers for the inspector drag handle
  const startResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStartY(e.clientY);
  }, []);

  const stopResize = useCallback(() => {
    setIsResizing(false);
  }, []);

  const handleResize = useCallback(
    (e) => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const deltaY = resizeStartY - e.clientY; // Inverted because we're measuring from bottom
      const newHeight = Math.max(
        200,
        Math.min(containerRect.height - 150, inspectorHeight + deltaY)
      );

      setInspectorHeight(newHeight);
      setResizeStartY(e.clientY);
    },
    [isResizing, resizeStartY, inspectorHeight]
  );

  // Global resize event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResize, { passive: false });
      document.addEventListener("mouseup", stopResize, { passive: false });
      document.body.style.userSelect = "none";
      document.body.style.cursor = "ns-resize";
    } else {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleResize, { passive: false });
      document.removeEventListener("mouseup", stopResize, { passive: false });
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isResizing, handleResize, stopResize]);

  // Calculate execution order and data flow (left-to-right layout)
  const { executionOrder, nodePositions, flowPaths } = useMemo(() => {
    const visited = new Set();
    const levelGroups = new Map(); // Group nodes by level
    const positions = new Map();
    const paths = [];

    // Topological sort for left-to-right execution order
    const visit = (nodeId, level = 0) => {
      if (visited.has(nodeId)) return level;
      visited.add(nodeId);

      // Add to level group
      if (!levelGroups.has(level)) {
        levelGroups.set(level, []);
      }
      levelGroups.get(level).push(nodeId);

      const outgoing = edges.filter((e) => e.source === nodeId);
      let maxChildLevel = level;

      outgoing.forEach((edge) => {
        const childLevel = visit(edge.target, level + 1);
        maxChildLevel = Math.max(maxChildLevel, childLevel);
        paths.push({
          id: `${edge.source}-${edge.target}`,
          source: edge.source,
          target: edge.target,
          sourceNode: nodes.find((n) => n.id === edge.source),
          targetNode: nodes.find((n) => n.id === edge.target),
        });
      });

      return maxChildLevel;
    };

    // Start from nodes with no incoming edges (leftmost nodes)
    const startNodes = nodes.filter((n) => !edges.find((e) => e.target === n.id));
    startNodes.forEach((n) => visit(n.id));

    // Calculate positions for left-to-right flow
    const executionOrder = [];
    levelGroups.forEach((nodeIds, level) => {
      nodeIds.forEach((nodeId, indexInLevel) => {
        const verticalCenter = nodeIds.length > 1 ? indexInLevel - (nodeIds.length - 1) / 2 : 0;

        positions.set(nodeId, {
          level: level, // horizontal position (left-to-right)
          verticalIndex: verticalCenter, // vertical position (centered)
        });
        executionOrder.push({ nodeId, level });
      });
    });

    return {
      executionOrder,
      nodePositions: positions,
      flowPaths: paths,
    };
  }, [nodes, edges]);

  // Get node icon and color based on type
  const getNodeVisualization = (node) => {
    if (!node) return { icon: DataObject, color: "#666", bgColor: "#333" }; // Default fallback
    const { type, data = {} } = node;
    const status = enhancedExecutionState[node.id]?.status || "idle";

    const typeConfig = {
      "trigger.manual": { icon: PlayArrow, color: "#4CAF50", bgColor: "#1B5E20" },
      "trigger.schedule": { icon: Schedule, color: "#FF9800", bgColor: "#E65100" },
      "data.model": { icon: DataObject, color: "#2196F3", bgColor: "#0D47A1" },
      "data.filter": { icon: FilterAlt, color: "#9C27B0", bgColor: "#4A148C" },
      "export.excel": { icon: Download, color: "#00BCD4", bgColor: "#006064" },
      default: { icon: Info, color: "#607D8B", bgColor: "#263238" },
    };

    const config = typeConfig[type] || typeConfig.default;

    const statusIcons = {
      running: CircularProgress,
      completed: CheckCircle,
      error: Error,
      warning: Warning,
      idle: config.icon || Info, // Fallback to Info icon
    };

    const statusColors = {
      running: "#FF9800",
      completed: "#4CAF50",
      error: "#F44336",
      warning: "#FF9800",
      idle: config.color || "#607D8B", // Fallback color
    };

    return {
      ...config,
      statusIcon: statusIcons[status] || Info, // Fallback to Info icon
      statusColor: statusColors[status] || "#607D8B", // Fallback color
      status,
    };
  };

  // Animate data flow - with toggle functionality
  const toggleDataFlow = () => {
    if (animatingFlow) {
      setAnimatingFlow(false);
    } else {
      setAnimatingFlow(true);
    }
  };

  // Render node with enhanced visualization
  const renderNode = (node, position) => {
    const viz = getNodeVisualization(node);
    const { data = {} } = node;
    const execution = enhancedExecutionState[node.id] || {};
    const isSelected = selectedNode === node.id;

    return (
      <Tooltip
        key={node.id}
        title={
          <Box sx={{ p: 1.2, minWidth: 180, maxWidth: 240 }}>
            {/* Header with node name and status */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: viz.statusColor,
                  fontWeight: 600,
                  fontSize: "11px",
                  flex: 1,
                }}
              >
                {data.label || node.type}
              </Typography>
              <Chip
                label={viz.status.toUpperCase()}
                size="small"
                sx={{
                  height: 16,
                  fontSize: "9px",
                  fontWeight: 600,
                  bgcolor: viz.statusColor + "20",
                  color: viz.statusColor,
                  border: `1px solid ${viz.statusColor}50`,
                  "& .MuiChip-label": { px: 0.8 },
                }}
              />
            </Box>

            {/* Essential metrics in compact format */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 0.8 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "9px",
                  fontFamily: "monospace",
                }}
              >
                ID: {node.id.length > 8 ? `${node.id.substring(0, 8)}...` : node.id}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "9px",
                  fontFamily: "monospace",
                }}
              >
                I/O: {edges.filter((e) => e.target === node.id).length}/
                {edges.filter((e) => e.source === node.id).length}
              </Typography>
            </Box>

            {/* Performance metrics if available */}
            {(execution.duration || execution.recordsProcessed) && (
              <Box sx={{ display: "flex", gap: 1.5, mb: 0.8 }}>
                {execution.duration && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.primary",
                      fontSize: "9px",
                      fontFamily: "monospace",
                    }}
                  >
                    ⏱ {execution.duration}ms
                  </Typography>
                )}
                {execution.recordsProcessed && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.primary",
                      fontSize: "9px",
                      fontFamily: "monospace",
                    }}
                  >
                    📊 {execution.recordsProcessed.toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}

            {/* Error indicator */}
            {execution.error && (
              <Typography
                variant="caption"
                sx={{
                  color: "error.main",
                  fontSize: "9px",
                  fontFamily: "monospace",
                  display: "block",
                  bgcolor: "error.main" + "10",
                  p: 0.5,
                  borderRadius: 0.5,
                  border: `1px solid ${theme.palette.error.main}30`,
                }}
              >
                ⚠ {execution.error.substring(0, 40)}...
              </Typography>
            )}

            {/* Click hint */}
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontSize: "8px",
                fontStyle: "italic",
                mt: 0.5,
                display: "block",
              }}
            >
              Click for details
            </Typography>
          </Box>
        }
        placement="top"
        arrow
        PopperProps={{
          disablePortal: false,
          sx: { zIndex: 2000 },
          modifiers: [
            {
              name: "preventOverflow",
              enabled: false, // Disable to keep tooltip always above
            },
            {
              name: "flip",
              enabled: false, // Disable flipping to keep it always above
            },
            {
              name: "offset",
              options: {
                offset: [0, 8], // Reduced from 10px to 8px - closer to node
              },
            },
            {
              name: "hide",
              enabled: false, // Disable hiding when out of view
            },
          ],
        }}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              backdropFilter: "blur(4px)",
              maxWidth: "none",
              "& .MuiTooltip-arrow": {
                color: "background.paper",
                "&::before": {
                  border: "1px solid",
                  borderColor: "divider",
                },
              },
            },
          },
        }}
      >
        <Paper
          className="workflow-node" // Add specific class for node identification
          onClick={() => setSelectedNode(isSelected ? null : node.id)}
          sx={{
            position: "absolute",
            left: `${80 + position.level * 220}px`, // Left-to-right spacing
            top: `${320 + position.verticalIndex * 140}px`, // Vertical centering with moderate top space for tooltips
            width: 100,
            height: 100,
            borderRadius: "16px",
            bgcolor: viz.bgColor,
            border: `2px solid ${viz.statusColor}`,
            cursor: "pointer",
            transition: "all 0.3s ease",
            animation: viz.status === "running" ? `${pulseAnimation} 2s infinite` : "none",
            transform: isSelected ? "scale(1.1)" : "scale(1)",
            boxShadow: isSelected ? `0 0 20px ${viz.statusColor}` : `0 4px 12px rgba(0,0,0,0.3)`,
            "&:hover": {
              transform: "scale(1.05)",
              boxShadow: `0 8px 25px rgba(0,0,0,0.4)`,
            },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 1.5,
          }}
        >
          {/* Status badge */}
          <Badge
            badgeContent={
              viz.status === "running" ? (
                <CircularProgress size={10} sx={{ color: viz.statusColor, position: "relative" }} />
              ) : viz.status === "completed" ? (
                "✓"
              ) : viz.status === "error" ? (
                "✗"
              ) : null
            }
            color={
              viz.status === "completed" ? "success" : viz.status === "error" ? "error" : "default"
            }
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: viz.statusColor,
                  width: 40,
                  height: 40,
                  mb: 1,
                  mx: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {(() => {
                  if (viz.status === "running") {
                    return <CircularProgress size={20} sx={{ color: "white" }} />;
                  }
                  const IconComponent = viz.statusIcon;
                  return IconComponent ? (
                    <IconComponent sx={{ fontSize: 20 }} />
                  ) : (
                    <Info sx={{ fontSize: 20 }} />
                  );
                })()}
              </Avatar>

              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: "10px",
                  lineHeight: 1.1,
                  display: "block",
                }}
              >
                {(data.label || node.type).substring(0, 12)}
                {(data.label || node.type).length > 12 && "..."}
              </Typography>

              {execution.recordsProcessed && (
                <Typography
                  variant="caption"
                  sx={{
                    color: viz.statusColor,
                    fontSize: "8px",
                    opacity: 0.8,
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  {execution.recordsProcessed > 1000
                    ? `${Math.round(execution.recordsProcessed / 1000)}k`
                    : execution.recordsProcessed}
                </Typography>
              )}
            </Box>
          </Badge>
        </Paper>
      </Tooltip>
    );
  };

  // Render animated data flow arrows (left-to-right)
  const renderDataFlow = (path) => {
    const sourcePos = nodePositions.get(path.source);
    const targetPos = nodePositions.get(path.target);

    if (!sourcePos || !targetPos) return null;

    // Calculate precise connection points
    const nodeWidth = 100;
    const nodeHeight = 100;
    const nodeBaseY = 320;
    const nodeSpacingY = 140;
    const nodeSpacingX = 220;
    const nodeBaseX = 80;

    const startX = nodeBaseX + sourcePos.level * nodeSpacingX + nodeWidth; // Right edge of source node
    const startY = nodeBaseY + sourcePos.verticalIndex * nodeSpacingY + nodeHeight / 2; // Exact vertical center of source node
    const endX = nodeBaseX + targetPos.level * nodeSpacingX; // Left edge of target node
    const endY = nodeBaseY + targetPos.verticalIndex * nodeSpacingY + nodeHeight / 2; // Exact vertical center of target node

    const isHovered = hoveredEdge === path.id;
    const isActive =
      enhancedExecutionState[path.source]?.status === "running" ||
      enhancedExecutionState[path.target]?.status === "running";

    // Control points for smooth horizontal curves
    const controlX1 = startX + 60;
    const controlY1 = startY;
    const controlX2 = endX - 60;
    const controlY2 = endY;

    return (
      <g key={path.id}>
        <defs>
          <marker
            id={`arrowhead-${path.id}`}
            markerWidth="8"
            markerHeight="6"
            refX="7"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <polygon
              points="0 0, 8 3, 0 6"
              fill={isActive ? "#00f6ff" : isHovered ? "#fff" : "#555"}
            />
          </marker>

          {/* Gradient for active connections */}
          {isActive && (
            <linearGradient id={`gradient-${path.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#00f6ff" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#00f6ff" stopOpacity="1" />
              <stop offset="100%" stopColor="#00f6ff" stopOpacity="0.3" />
            </linearGradient>
          )}
        </defs>

        {/* Connection path */}
        <path
          d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
          stroke={isActive ? `url(#gradient-${path.id})` : isHovered ? "#fff" : "#555"}
          strokeWidth={isActive ? 3 : isHovered ? 2 : 1.5}
          fill="none"
          markerEnd={`url(#arrowhead-${path.id})`}
          style={{
            filter: isActive ? "drop-shadow(0 0 4px #00f6ff)" : "none",
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={() => setHoveredEdge(path.id)}
          onMouseLeave={() => setHoveredEdge(null)}
        />

        {/* Animated data flow particles */}
        {(animatingFlow || isActive) && (
          <circle
            r="2.5"
            fill="#00f6ff"
            style={{
              filter: "drop-shadow(0 0 3px #00f6ff)",
            }}
          >
            <animateMotion
              dur="1.5s"
              repeatCount="indefinite"
              path={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
            />
          </circle>
        )}

        {/* Connection label on hover */}
        {isHovered && (
          <text
            x={(startX + endX) / 2}
            y={(startY + endY) / 2 - 15}
            fill="#fff"
            fontSize="11"
            textAnchor="middle"
            style={{
              pointerEvents: "none",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))",
            }}
          >
            {`${path.sourceNode?.data?.label || "Node"} → ${path.targetNode?.data?.label || "Node"}`}
          </text>
        )}
      </g>
    );
  };

  // Calculate workflow dimensions for left-to-right layout
  const maxLevel = Math.max(...Array.from(nodePositions.values()).map((p) => p.level), 0);
  const maxVertical = Math.max(
    ...Array.from(nodePositions.values()).map((p) => Math.abs(p.verticalIndex)),
    0
  );
  const workflowWidth = Math.max(900, (maxLevel + 1) * 220 + 160);

  // Use a consistent height when there are no nodes for better empty state display
  const workflowHeight =
    nodes.length === 0
      ? 500 // Fixed height for empty state
      : Math.max(550, (maxVertical * 2 + 1) * 140 + 450);

  // Use real execution state without generating fake data
  const enhancedExecutionState = useMemo(() => {
    // Return the actual execution state from SignalR events without any fake enhancements
    return { ...executionState };
  }, [executionState]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 40px)",
        backgroundColor: "background.default",
        overflow: "hidden", // Prevent page scrolling
        position: "relative",
      }}
    >
      {/* Header - Clean and minimalistic */}
      <Box
        sx={{
          p: 3,
          pb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          position: "sticky",
          top: "20px",
          zIndex: 1000,
        }}
      >
        <Box sx={{ position: "relative", left: 20 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: "bold" }}
          >
            Data flow visualization
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            position: "relative",
            right: 10,
            top: 5,
          }}
        >
          {/* Only show node/edge counts when there are actually nodes */}
          {nodes.length > 0 && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "12px", textTransform: "uppercase" }}
                >
                  {nodes.length} nodes
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "secondary.main" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "12px", textTransform: "uppercase" }}
                >
                  {edges.length} connections
                </Typography>
              </Box>
            </>
          )}

          {/* Toggle animation button */}
          {nodes.length > 0 && (
            <IconButton
              onClick={toggleDataFlow}
              size="small"
              sx={{
                color: animatingFlow ? "warning.main" : "primary.main",
                bgcolor: animatingFlow ? "warning.light" : "action.hover",
                border: "1px solid",
                borderColor: animatingFlow ? "warning.main" : "divider",
                "&:hover": {
                  bgcolor: animatingFlow ? "warning.main" : "action.selected",
                  borderColor: animatingFlow ? "warning.dark" : "primary.main",
                  color: animatingFlow ? "warning.contrastText" : "primary.main",
                },
              }}
            >
              {animatingFlow ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Workflow Canvas */}
      <Card
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          top: -25, // Offset for header
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0, // Allow flex item to shrink
          mx: 3, // Horizontal margin
          mb: 3, // Bottom margin
        }}
      >
        {/* Fixed panels - execution order and performance metrics - only show when there are nodes */}
        {nodes.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 86,
              right: 16,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pointerEvents: "none", // Allow clicks to pass through to canvas
            }}
          >
            {/* Performance metrics - fixed position */}
            {Object.keys(enhancedExecutionState).length > 0 && (
              <Box
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  backdropFilter: "blur(10px)",
                  minWidth: 180,
                  maxWidth: 220,
                  boxShadow: 2,
                  pointerEvents: "auto", // Re-enable pointer events for this specific panel
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "success.main",
                    fontWeight: 600,
                    mb: 1,
                    display: "block",
                    fontSize: "11px",
                  }}
                >
                  PERFORMANCE METRICS
                </Typography>

                {Object.entries(enhancedExecutionState)
                  .slice(0, 4)
                  .map(([nodeId, state]) => {
                    const node = nodes.find((n) => n.id === nodeId);
                    if (!state.duration && !state.recordsProcessed) return null;

                    return (
                      <Box key={nodeId} sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.primary",
                            fontSize: "10px",
                            display: "block",
                            fontWeight: 500,
                          }}
                        >
                          {(node?.data?.label || nodeId).substring(0, 18)}
                          {(node?.data?.label || nodeId).length > 18 && "..."}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 0.25 }}>
                          {state.duration && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "warning.main",
                                fontSize: "9px",
                                fontFamily: "monospace",
                              }}
                            >
                              {state.duration}ms
                            </Typography>
                          )}
                          {state.recordsProcessed && (
                            <Typography
                              variant="caption"
                              sx={{ color: "info.main", fontSize: "9px", fontFamily: "monospace" }}
                            >
                              {state.recordsProcessed > 1000
                                ? `${Math.round(state.recordsProcessed / 1000)}k`
                                : state.recordsProcessed}{" "}
                              rec
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            )}

            {/* Execution flow legend - fixed position */}
            {executionOrder.length > 0 && (
              <Box
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  backdropFilter: "blur(10px)",
                  maxWidth: 200,
                  boxShadow: 2,
                  pointerEvents: "auto", // Re-enable pointer events for this specific panel
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    mb: 1,
                    display: "block",
                    fontSize: "11px",
                  }}
                >
                  EXECUTION ORDER
                </Typography>
                {executionOrder.slice(0, 5).map(({ nodeId }, index) => {
                  const node = nodes.find((n) => n.id === nodeId);
                  if (!node) return null; // Skip if node not found
                  const viz = getNodeVisualization(node);
                  return (
                    <Box key={nodeId} sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: viz.statusColor,
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.primary", fontSize: "10px", fontFamily: "monospace" }}
                      >
                        {index + 1}.{" "}
                        {(node?.data?.label || node?.type || "Unknown").substring(0, 15)}
                        {(node?.data?.label || node?.type || "Unknown").length > 15 && "..."}
                      </Typography>
                    </Box>
                  );
                })}
                {executionOrder.length > 5 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "9px", mt: 0.5, fontStyle: "italic" }}
                  >
                    +{executionOrder.length - 5} more nodes
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        <CardContent
          ref={canvasRef}
          data-workflow-canvas="true"
          onMouseDown={nodes.length > 0 ? handleMouseDown : undefined}
          onContextMenu={(e) => e.preventDefault()} // Prevent context menu on right click
          onAuxClick={(e) => e.preventDefault()} // Prevent auxiliary click behaviors (middle mouse)
          sx={{
            p: 3,
            pt: 4,
            position: "relative",
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0, // Allow flex item to shrink
            cursor:
              nodes.length > 0 && isDragging ? "grabbing" : nodes.length > 0 ? "grab" : "default",
            userSelect: "none",
            // Ensure events are properly handled
            touchAction: "none",
            // When inspector is open, adjust the content height
            height: selectedNode ? `calc(100% - ${inspectorHeight + 40}px)` : "100%",
          }}
        >
          {/* Workflow visualization area - flexible height */}
          <Box
            sx={{
              flex: "1 1 100%",
              minHeight: nodes.length === 0 ? "400px" : "300px", // Minimum height for workflow area
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Subtle background grid - only show when there are nodes */}
            {nodes.length > 0 && (
              <Box
                className="workflow-background"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: (theme) => `
                    linear-gradient(${theme.palette.divider} 1px, transparent 1px),
                    linear-gradient(90deg, ${theme.palette.divider} 1px, transparent 1px)
                  `,
                  backgroundSize: `${40 * transform.scale}px ${40 * transform.scale}px`,
                  backgroundPosition: `${transform.x}px ${transform.y}px`,
                  opacity: 0.3,
                }}
              />
            )}

            {/* Transformable container for workflow content - only when there are nodes */}
            {nodes.length > 0 && (
              <Box
                sx={{
                  position: "relative",
                  transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                  transformOrigin: "0 0",
                  transition: isDragging ? "none" : "transform 0.1s ease-out",
                  width: workflowWidth,
                  height: workflowHeight,
                }}
              >
                {/* SVG for connections */}
                <svg
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                >
                  {flowPaths.map(renderDataFlow)}
                </svg>

                {/* Nodes */}
                <Box sx={{ position: "relative", zIndex: 2 }}>
                  {executionOrder.map(({ nodeId }) => {
                    const node = nodes.find((n) => n.id === nodeId);
                    const position = nodePositions.get(nodeId);
                    return node && position ? renderNode(node, position) : null;
                  })}
                </Box>
              </Box>
            )}

            {/* Empty state - centered when no nodes exist */}
            {nodes.length === 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                  zIndex: 10,
                  pointerEvents: "none",
                }}
              >
                <DataObject
                  sx={{
                    fontSize: 80,
                    mb: 3,
                    opacity: 0.4,
                    color: "text.secondary",
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    mb: 2,
                    color: "text.primary",
                    fontWeight: 500,
                  }}
                >
                  No Workflow Defined
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    maxWidth: "300px",
                  }}
                >
                  Switch to the "AGENTIC Editor" tab to start building your workflow by adding nodes
                </Typography>
              </Box>
            )}
          </Box>

          {/* Selected node details - Technical deep dive - positioned below the workflow visualization */}
          {selectedNode && (
            <>
              {/* Resize handle - positioned at the top edge of the inspector */}
              <Box
                onMouseDown={startResize}
                sx={{
                  position: "absolute",
                  bottom: inspectorHeight + 15, // Position above the inspector
                  left: "50%",
                  transform: "translateX(-50%)",
                  height: 10,
                  width: 60,
                  borderRadius: "5px",
                  backgroundColor: "divider",
                  cursor: "ns-resize",
                  opacity: 0.7,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 15,
                  transition: isResizing ? "none" : "opacity 0.2s, background-color 0.2s",
                  "&:hover": {
                    opacity: 1,
                    backgroundColor: "primary.main",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "background.paper",
                    fontSize: 14,
                    userSelect: "none",
                    fontWeight: 600,
                    lineHeight: 1,
                  }}
                >
                  ⋮⋮⋮
                </Typography>
              </Box>

              <Box
                sx={{
                  overflow: "hidden",
                  position: "absolute",
                  bottom: 20,
                  left: 20,
                  right: 20,
                  height: `${inspectorHeight}px`,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.default",
                  p: 2,

                  zIndex: 99,
                  transition: isResizing ? "none" : "height 0.2s ease-out",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "text.primary",
                      fontFamily: "monospace",
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <DataObject sx={{ color: "text.secondary" }} />
                    NODE INSPECTOR:{" "}
                    {nodes.find((n) => n.id === selectedNode)?.data?.label || selectedNode}
                  </Typography>

                  <IconButton
                    onClick={() => setSelectedNode(null)}
                    size="small"
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        bgcolor: "action.hover",
                        color: "text.primary",
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(30%, 1fr))",
                    gap: 2,
                    height: "calc(100% - 60px)",
                    overflow: "auto",
                  }}
                >
                  {/* Node Overview - matches tooltip structure */}
                  <Paper
                    sx={{
                      p: 2.5,
                      bgcolor: "background.default",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      height: `calc((${inspectorHeight}px - 95px) )`, // Half available space minus padding/header
                      overflow: "auto",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "text.primary", mb: 2, fontWeight: 600, flexShrink: 0 }}
                    >
                      📊 NODE OVERVIEW
                    </Typography>

                    <Box sx={{ flex: 1, overflow: "auto" }}>
                      {/* Technical Details Grid - exactly same as tooltip */}
                      <Box
                        sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              display: "block",
                              fontSize: "10px",
                              fontWeight: 600,
                            }}
                          >
                            NODE ID
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.primary",
                              fontFamily: "monospace",
                              fontSize: "11px",
                            }}
                          >
                            {selectedNode}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              display: "block",
                              fontSize: "10px",
                              fontWeight: 600,
                            }}
                          >
                            TYPE
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.primary",
                              fontFamily: "monospace",
                              fontSize: "11px",
                            }}
                          >
                            {nodes.find((n) => n.id === selectedNode)?.type}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              display: "block",
                              fontSize: "10px",
                              fontWeight: 600,
                            }}
                          >
                            STATUS
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: (() => {
                                const node = nodes.find((n) => n.id === selectedNode);
                                const viz = getNodeVisualization(node);
                                return viz.statusColor;
                              })(),
                              fontWeight: 600,
                              fontSize: "11px",
                            }}
                          >
                            {(() => {
                              const node = nodes.find((n) => n.id === selectedNode);
                              const viz = getNodeVisualization(node);
                              return viz.status.toUpperCase();
                            })()}
                          </Typography>
                        </Box>

                        {enhancedExecutionState[selectedNode]?.duration && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                display: "block",
                                fontSize: "10px",
                                fontWeight: 600,
                              }}
                            >
                              EXECUTION TIME
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.primary",
                                fontFamily: "monospace",
                                fontSize: "11px",
                              }}
                            >
                              {enhancedExecutionState[selectedNode].duration}ms
                            </Typography>
                          </Box>
                        )}

                        {enhancedExecutionState[selectedNode]?.recordsProcessed && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                display: "block",
                                fontSize: "10px",
                                fontWeight: 600,
                              }}
                            >
                              RECORDS PROCESSED
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.primary",
                                fontFamily: "monospace",
                                fontSize: "11px",
                              }}
                            >
                              {enhancedExecutionState[
                                selectedNode
                              ].recordsProcessed.toLocaleString()}
                            </Typography>
                          </Box>
                        )}

                        {enhancedExecutionState[selectedNode]?.startTime && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                display: "block",
                                fontSize: "10px",
                                fontWeight: 600,
                              }}
                            >
                              LAST EXECUTION
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.primary",
                                fontFamily: "monospace",
                                fontSize: "11px",
                              }}
                            >
                              {new Date(
                                enhancedExecutionState[selectedNode].startTime
                              ).toISOString()}
                            </Typography>
                          </Box>
                        )}

                        {enhancedExecutionState[selectedNode]?.memoryUsage && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                display: "block",
                                fontSize: "10px",
                                fontWeight: 600,
                              }}
                            >
                              MEMORY USAGE
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.primary",
                                fontFamily: "monospace",
                                fontSize: "11px",
                              }}
                            >
                              {Math.round(
                                enhancedExecutionState[selectedNode].memoryUsage / 1024 / 1024
                              )}
                              MB
                            </Typography>
                          </Box>
                        )}

                        {enhancedExecutionState[selectedNode]?.error && (
                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "error.main",
                                display: "block",
                                fontSize: "10px",
                                fontWeight: 600,
                              }}
                            >
                              ERROR
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "error.main",
                                fontFamily: "monospace",
                                fontSize: "10px",
                              }}
                            >
                              {enhancedExecutionState[selectedNode].error.substring(0, 100)}...
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Connection Info - same as tooltip */}
                    <Box sx={{ mt: 1.5, flexShrink: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mb: 0.5,
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      >
                        CONNECTIONS
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.primary", fontSize: "10px", fontFamily: "monospace" }}
                        >
                          IN: {edges.filter((e) => e.target === selectedNode).length}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.primary", fontSize: "10px", fontFamily: "monospace" }}
                        >
                          OUT: {edges.filter((e) => e.source === selectedNode).length}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                  {/* Raw Configuration Dump - Additional detail for inspector */}
                  <Paper
                    sx={{
                      p: 2.5,
                      bgcolor: "background.default",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      height: `calc((${inspectorHeight}px - 95px) )`, // Half available space minus padding/header
                      overflow: "auto",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "text.primary", mb: 2, fontWeight: 600, flexShrink: 0 }}
                    >
                      🔧 FULL CONFIGURATION DUMP
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        overflow: "auto",
                        bgcolor: "background.paper",
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <pre
                        style={{
                          fontSize: "10px",
                          color: "var(--mui-palette-text-primary)",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                          fontFamily: "monospace",
                          lineHeight: 1.4,
                        }}
                      >
                        {JSON.stringify(
                          nodes.find((n) => n.id === selectedNode)?.data || {},
                          (key, value) => {
                            if (typeof value === "function") {
                              return "[Function]";
                            }
                            return value;
                          },
                          2
                        )}
                      </pre>
                    </Box>
                  </Paper>
                  {/* Preview Output - same as tooltip but expanded */}
                  {(enhancedExecutionState[selectedNode]?.output ||
                    (nodes.find((n) => n.id === selectedNode)?.data?.output &&
                      Object.keys(nodes.find((n) => n.id === selectedNode).data.output).length >
                        0)) && (
                    <Paper
                      sx={{
                        p: 2.5,
                        bgcolor: "background.default",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        height: `calc((${inspectorHeight}px - 95px) )`, // Half available space minus padding/header   overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "text.primary", mb: 2, fontWeight: 600, flexShrink: 0 }}
                      >
                        🔍{" "}
                        {enhancedExecutionState[selectedNode]?.output
                          ? "EXECUTION OUTPUT"
                          : "CONFIGURATION OUTPUT"}
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "rgba(76, 175, 80, 0.05)",
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "success.main",
                          borderStyle: "dashed",
                          flex: 1,
                          overflow: "auto",
                        }}
                      >
                        <pre
                          style={{
                            fontSize: "10px",
                            color: "var(--mui-palette-text-primary)",
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            lineHeight: 1.4,
                          }}
                        >
                          {(() => {
                            const output =
                              enhancedExecutionState[selectedNode]?.output ||
                              nodes.find((n) => n.id === selectedNode)?.data?.output;
                            if (typeof output === "function") {
                              return "[Function - cannot display]";
                            }
                            if (typeof output === "object" && output !== null) {
                              try {
                                return JSON.stringify(
                                  output,
                                  (key, value) => {
                                    if (typeof value === "function") {
                                      return "[Function]";
                                    }
                                    return value;
                                  },
                                  2
                                );
                              } catch (e) {
                                return "[Object - cannot serialize]";
                              }
                            }
                            return String(output);
                          })()}
                        </pre>
                      </Box>
                    </Paper>
                  )}
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WorkflowView;
